/**
 * Versioned controlled dictionaries for the PromoLanding business application.
 *
 * The form MUST NOT accept free-text for business type, category, or city —
 * every controlled value comes from these dictionaries so the client mirrors
 * the server contract (see the one-common-repo decisions
 * "Справочник категорий бизнеса MVP" / "Поля короткой заявки бизнеса на PromoLanding").
 *
 * NOTE (pending PLI-78): the final wire identifiers and dictionary versions are
 * finalized by the public merchant-application OpenAPI contract. The category
 * codes and version strings below track the approved MVP decision and are
 * best-effort until PLI-78 locks the contract.
 */

/** Bump when the category list or any code changes; sent alongside the value. */
export const categoryDictionaryVersion = 'mvp-2026-07-20';

/** Bump when the city list changes; sent alongside the value. */
export const cityDictionaryVersion = 'mvp-2026-07-20';

export interface DictionaryEntry {
  /** Stable code stored server-side (never the localized label). */
  readonly id: string;
  /** i18n key resolved to the user-facing label at the component edge. */
  readonly labelKey: string;
}

/**
 * Temporary controlled MVP category dictionary (10 entries) from
 * "Decision - Справочник категорий бизнеса MVP" (approved 2026-07-20).
 * Codes are authoritative for MVP; localized labels live in the locale files.
 */
export const businessCategories = [
  { id: 'FOOD_SERVICE', labelKey: 'applyForm.categories.FOOD_SERVICE' },
  { id: 'RETAIL', labelKey: 'applyForm.categories.RETAIL' },
  { id: 'BEAUTY_HEALTH', labelKey: 'applyForm.categories.BEAUTY_HEALTH' },
  { id: 'SERVICES', labelKey: 'applyForm.categories.SERVICES' },
  { id: 'FUEL', labelKey: 'applyForm.categories.FUEL' },
  { id: 'PHARMACY', labelKey: 'applyForm.categories.PHARMACY' },
  { id: 'FASHION', labelKey: 'applyForm.categories.FASHION' },
  { id: 'ELECTRONICS', labelKey: 'applyForm.categories.ELECTRONICS' },
  { id: 'ENTERTAINMENT', labelKey: 'applyForm.categories.ENTERTAINMENT' },
  { id: 'OTHER', labelKey: 'applyForm.categories.OTHER' },
] as const satisfies ReadonlyArray<DictionaryEntry>;

export type BusinessCategoryId = (typeof businessCategories)[number]['id'];

/** Non-empty tuple of category codes for a zod enum. */
export const businessCategoryIds = businessCategories.map((c) => c.id) as [
  BusinessCategoryId,
  ...Array<BusinessCategoryId>,
];

/**
 * MVP city dictionary. The MVP is Almaty-only; free-text cities are rejected
 * (decision "Поля короткой заявки бизнеса на PromoLanding").
 */
export const cities = [
  { id: 'ALMATY', labelKey: 'applyForm.cities.ALMATY' },
] as const satisfies ReadonlyArray<DictionaryEntry>;

export type CityId = (typeof cities)[number]['id'];

export const cityIds = cities.map((c) => c.id) as [CityId, ...Array<CityId>];

/** The single MVP city; sent as `city_ids: ['ALMATY']`. */
export const defaultCityId: CityId = 'ALMATY';
